import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Checkbox, message, Avatar, Row, Col, Spin, Card, Typography, Divider, Tag } from 'antd';
import { 
  UserOutlined, 
  PhoneOutlined, 
  LockOutlined,
  EditOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import './index.css'; 
import profile from '../../services/profile';
import moment from 'moment';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

const { Title, Text } = Typography;

const ProfilePage = () => {
  const [form] = Form.useForm();
  const [changePassword, setChangePassword] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const queryClient = useQueryClient();
  const { currentUser } = useSelector((state) => state.user);

  // Only fetch additional profile data if we don't have it in Redux
  const { data: profileData, isLoading, refetch } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const data = await profile.get();
      return data.data;
    },
    enabled: !currentUser?.phone || !currentUser?.address // Only fetch if we don't have these fields
  });

  useEffect(() => {
    // Use Redux data first, then fallback to API data
    const formData = {
      name: currentUser?.name,
      last_name: currentUser?.last_name,
      email: currentUser?.email,
      ...profileData // Additional fields from API
    };
    
    if (formData) {
      form.setFieldsValue(formData);
    }
  }, [profileData, currentUser, form]);

  const handleFinish = async (values) => {
    setBtnLoading(true);
    try {
      await profile.update(values);
      message.success('Profile updated successfully!');
      refetch();
    } catch (error) {
      message.error('Failed to update profile!');
    } finally {
      setBtnLoading(false);
    }
  };

  const toggleChangePassword = (e) => {
    setChangePassword(e.target.checked);
  };

  // Get initials for avatar
  const getInitials = () => {
    const first = currentUser?.name?.[0] || '';
    const last = currentUser?.last_name?.[0] || '';
    return (first + last).toUpperCase() || null;
  };

  return (
    <div className="page-container" style={{
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
      minHeight: '100vh',
      padding: '24px'
    }}>
      <Spin spinning={isLoading}>
        {/* Page Header */}
        <div className="page-header" style={{
          marginBottom: '32px',
          padding: '24px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle at top right, rgba(24,144,255,0.1) 0%, rgba(24,144,255,0) 70%)',
            pointerEvents: 'none'
          }} />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '24px',
            position: 'relative',
            zIndex: 1
          }}>
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(24,144,255,0.25)',
                  transition: 'transform 0.3s ease'
                }}>
                  <UserOutlined style={{ fontSize: '20px', color: '#fff' }} />
                </div>
                <Typography.Title level={2} style={{
                  margin: 0,
                  color: '#1f1f1f',
                  background: 'linear-gradient(135deg, #1f1f1f 0%, #434343 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: '24px'
                }}>
                  Profile Settings
                </Typography.Title>
              </div>
              <Typography.Text type="secondary" style={{ fontSize: '12px', marginLeft: '52px' }}>
                Manage your personal information and account settings
              </Typography.Text>
            </div>
          </div>
        </div>

        <Row gutter={[24, 24]}>
          {/* Account Info Card */}
          <Col xs={24} md={8}>
            <Card className="shadow-sm" style={{
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                textAlign: 'center', 
                marginBottom: '24px' 
              }}>
                <Avatar
                  size={120}
                  style={{
                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                    color: '#fff',
                    fontSize: 48,
                    fontWeight: 700,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  icon={!getInitials() && <UserOutlined />}
                >
                  {getInitials()}
                </Avatar>
                <Title level={4} style={{ margin: '8px 0' }}>
                  {currentUser?.name} {currentUser?.last_name}
                </Title>
                <Text type="secondary">{currentUser?.email}</Text>
              </div>

              <Divider style={{ margin: '16px 0' }} />

              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <CalendarOutlined style={{ color: '#1890ff' }} />
                  <Text type="secondary">Member since</Text>
                </div>
                <Text strong>{moment(form.getFieldValue('created_at')).format('MMMM D, YYYY')}</Text>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <SafetyOutlined style={{ color: '#52c41a' }} />
                  <Text type="secondary">Account Status</Text>
                </div>
                <Tag color="success">Active</Tag>
              </div>
            </Card>
          </Col>

          {/* Profile Form Card */}
          <Col xs={24} md={16}>
            <Card className="shadow-sm" style={{
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                className="profile-form"
              >
                <div className="form-section">
                  <Title level={5} style={{ marginBottom: '24px' }}>Personal Information</Title>
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="name"
                        label="Full Name"
                        rules={[{ required: true, message: 'Please enter your name' }]}
                      >
                        <Input 
                          size="large" 
                          prefix={<UserOutlined />} 
                          placeholder="Enter your full name"
                          style={{ borderRadius: '8px' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="last_name"
                        label="Last Name"
                        rules={[{ required: true, message: 'Please enter your last name' }]}
                      >
                        <Input 
                          size="large" 
                          prefix={<UserOutlined />} 
                          placeholder="Enter your last name"
                          style={{ borderRadius: '8px' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="phone"
                        label="Phone Number"
                      >
                        <Input 
                          size="large" 
                          prefix={<PhoneOutlined />} 
                          placeholder="Enter your phone number"
                          style={{ borderRadius: '8px' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="address"
                        label="Address"
                        rules={[{ required: true, message: 'Please input your address!' }]}
                      >
                        <Input 
                          size="large"
                          prefix={<EnvironmentOutlined />}
                          placeholder="Address"
                          style={{ borderRadius: '8px' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                <Divider style={{ margin: '32px 0' }} />

                <div className="form-section">
                  <Title level={5} style={{ marginBottom: '24px' }}>Password Settings</Title>
                  <div className="password-toggle" style={{ marginBottom: '24px' }}>
                    <Checkbox 
                      checked={changePassword}
                      onChange={toggleChangePassword}
                      size="large"
                    >
                      Change Password
                    </Checkbox>
                  </div>

                  {changePassword && (
                    <Row gutter={16}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="current_password"
                          label="Current Password"
                          rules={[{ required: true, message: 'Please enter current password' }]}
                        >
                          <Input.Password 
                            size="large" 
                            prefix={<LockOutlined />} 
                            placeholder="Enter current password"
                            style={{ borderRadius: '8px' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="new_password"
                          label="New Password"
                          rules={[{ required: true, message: 'Please enter new password' }]}
                        >
                          <Input.Password 
                            size="large" 
                            prefix={<LockOutlined />} 
                            placeholder="Enter new password"
                            style={{ borderRadius: '8px' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </div>

                <div className="form-actions" style={{ 
                  marginTop: '32px',
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={btnLoading}
                    size="large"
                    icon={<EditOutlined />}
                    style={{
                      height: '48px',
                      padding: '0 32px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                      border: 'none',
                      boxShadow: '0 2px 8px rgba(24,144,255,0.25)'
                    }}
                  >
                    Update Profile
                  </Button>
                </div>
              </Form>
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default ProfilePage;
